'use client';

import {
  ClientOnly,
  IconButton,
  Skeleton,
  Span,
  type IconButtonProps,
  type SpanProps,
} from '@chakra-ui/react';
import { default as MoonIcon } from '~icons/lucide/moon';
import { default as SunIcon } from '~icons/lucide/sun';
import { ThemeProvider, useTheme, type ThemeProviderProps } from 'next-themes';

type ColorModeProviderProps = ThemeProviderProps;

export function ColorModeProvider(props: ColorModeProviderProps) {
  return <ThemeProvider attribute="class" disableTransitionOnChange {...props} />;
}

export type ColorMode = 'light' | 'dark';

type UseColorModeReturn = {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  toggleColorMode: () => void;
};

export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, setTheme } = useTheme();
  const toggleColorMode = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return {
    colorMode: resolvedTheme as ColorMode,
    setColorMode: setTheme,
    toggleColorMode,
  };
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? dark : light;
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? <MoonIcon /> : <SunIcon />;
}

type ColorModeButtonProps = Omit<IconButtonProps, 'aria-label'> & {
  ref?: React.RefObject<HTMLButtonElement>;
};

export const ColorModeButton = ({ ref, ...props }: ColorModeButtonProps) => {
  const { toggleColorMode } = useColorMode();

  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="ghost"
        aria-label="Toggle color mode"
        size="sm"
        ref={ref}
        {...props}
        css={{
          _icon: {
            width: '5',
            height: '5',
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  );
};

type LightModeProps = SpanProps & {
  ref?: React.RefObject<HTMLSpanElement>;
};

export const LightMode = ({ ref, ...props }: LightModeProps) => {
  return (
    <Span
      color="fg"
      display="contents"
      className="chakra-theme light"
      colorPalette="light"
      ref={ref}
      {...props}
    />
  );
};

type DarkModeProps = SpanProps & {
  ref?: React.RefObject<HTMLSpanElement>;
};

export const DarkMode = ({ ref, ...props }: DarkModeProps) => {
  return <Span color="fg" display="contents" className="chakra-theme dark" ref={ref} {...props} />;
};
